import type { LucideIcon } from 'lucide-react'
import {
  BookOpen,
  Calculator,
  ClipboardCheck,
  CreditCard,
  FileCheck,
  Handshake,
  History,
  Landmark,
  Megaphone,
  Receipt,
  Ship,
  ShoppingCart,
  TrendingUp,
  Users,
  Warehouse,
  Wrench,
} from 'lucide-react'

import { DEAL_STAGES, STAGE_LABELS } from '@/types/deal'
import { CONSIGNMENT_STATUSES, CONSIGNMENT_STATUS_LABELS } from '@/types/consignment'
import { INVOICE_STATUS_LABELS, SOURCE_LABELS } from '@/types/finance'
import { REGISTRATION_STATUS_LABELS } from '@/types/documents'
import { LEAD_STATUS_LABELS } from '@/types/marketing'
import type { Flow } from '@/lib/flows'

export type ReportSelectFilter = {
  kind: 'select'
  param: string
  label: string
  options: { value: string; label: string }[]
}

export type ReportCheckboxFilter = {
  kind: 'checkbox'
  param: string
  label: string
}

export type ReportDateRangeFilter = {
  kind: 'daterange'
  label: string
  fromParam: string
  toParam: string
}

export type ReportFilter = ReportSelectFilter | ReportCheckboxFilter | ReportDateRangeFilter

export type ReportDef = {
  id: string
  label: string
  description: string
  endpoint: string
  filename: string
  icon: LucideIcon
  // The flow whose data the report is built from — the report is only shown
  // (and its export only works) for users granted that flow.
  flow: Flow
  searchPlaceholder?: string
  filters: ReportFilter[]
}

export type ReportFilterValues = Record<string, string | boolean | undefined>

// The report catalog mirrors the backend export endpoints one-to-one; each
// report's filters accept the same query params as its module's list page.
export const REPORTS: ReportDef[] = [
  {
    id: 'boats',
    flow: 'inventory',
    label: 'Inventory',
    description: 'Boat inventory with pricing, specs, and listing state.',
    endpoint: '/inventory/boats/export',
    filename: 'boats',
    icon: Ship,
    searchPlaceholder: 'Search make or model…',
    filters: [
      {
        kind: 'select',
        param: 'listing_state',
        label: 'Listings',
        options: [
          { value: 'for_sale', label: 'For Sale' },
          { value: 'sold', label: 'Sold' },
          { value: 'off_market', label: 'Off Market' },
        ],
      },
    ],
  },
  {
    id: 'work-orders',
    flow: 'service',
    label: 'Work Orders',
    description: 'Service work orders with status, priority, and dates.',
    endpoint: '/service/work-orders/export',
    filename: 'work-orders',
    icon: Wrench,
    searchPlaceholder: 'Search title or description…',
    filters: [
      {
        kind: 'select',
        param: 'status',
        label: 'Statuses',
        options: [
          { value: 'pending', label: 'Pending' },
          { value: 'in_progress', label: 'In Progress' },
          { value: 'completed', label: 'Completed' },
          { value: 'on_hold', label: 'On Hold' },
        ],
      },
      {
        kind: 'select',
        param: 'priority',
        label: 'Priorities',
        options: [
          { value: 'low', label: 'Low' },
          { value: 'medium', label: 'Medium' },
          { value: 'high', label: 'High' },
          { value: 'urgent', label: 'Urgent' },
        ],
      },
      { kind: 'checkbox', param: 'open_only', label: 'Open only' },
    ],
  },
  {
    id: 'customers',
    flow: 'sales',
    label: 'Customers',
    description: 'Customer contacts with status.',
    endpoint: '/customers/export',
    filename: 'customers',
    icon: Users,
    searchPlaceholder: 'Search name, email, or phone…',
    filters: [
      {
        kind: 'select',
        param: 'status',
        label: 'Statuses',
        options: [
          { value: 'lead', label: 'Lead' },
          { value: 'active', label: 'Active' },
          { value: 'inactive', label: 'Inactive' },
        ],
      },
    ],
  },
  {
    id: 'deals',
    flow: 'sales',
    label: 'Deals',
    description: 'Sales pipeline with values, stages, and expected close dates.',
    endpoint: '/deals/export',
    filename: 'deals',
    icon: TrendingUp,
    searchPlaceholder: 'Search deals…',
    filters: [
      {
        kind: 'select',
        param: 'stage',
        label: 'Stages',
        options: DEAL_STAGES.map((stage) => ({ value: stage, label: STAGE_LABELS[stage] })),
      },
    ],
  },
  {
    id: 'consignments',
    flow: 'inventory',
    label: 'Consignments',
    description: 'Consignment agreements with commission and settlement figures.',
    endpoint: '/inventory/consignments/export',
    filename: 'consignments',
    icon: Handshake,
    searchPlaceholder: 'Search boat or owner…',
    filters: [
      {
        kind: 'select',
        param: 'status',
        label: 'Statuses',
        options: CONSIGNMENT_STATUSES.map((status) => ({
          value: status,
          label: CONSIGNMENT_STATUS_LABELS[status],
        })),
      },
    ],
  },
  {
    id: 'slips',
    flow: 'storage',
    label: 'Slips',
    description: 'Slip and storage inventory with occupancy status.',
    endpoint: '/storage/slips/export',
    filename: 'slips',
    icon: Warehouse,
    filters: [
      {
        kind: 'select',
        param: 'type',
        label: 'Types',
        options: [
          { value: 'wet', label: 'Wet' },
          { value: 'dry', label: 'Dry' },
          { value: 'warehouse', label: 'Warehouse' },
        ],
      },
    ],
  },
  {
    id: 'allocations',
    flow: 'storage',
    label: 'Storage Allocations',
    description: 'Boat-to-slip assignment history.',
    endpoint: '/storage/allocations/export',
    filename: 'allocations',
    icon: History,
    filters: [{ kind: 'checkbox', param: 'active', label: 'Active only' }],
  },
  {
    id: 'pnl',
    flow: 'finance',
    label: 'Profit & Loss',
    description: 'Income vs. expenses by category for a period, with net income.',
    endpoint: '/finance/pnl/export',
    filename: 'pnl',
    icon: Calculator,
    filters: [{ kind: 'daterange', label: 'Period', fromParam: 'from', toParam: 'to' }],
  },
  {
    id: 'transactions',
    flow: 'finance',
    label: 'Ledger Transactions',
    description: 'Every income and expense entry with category and source.',
    endpoint: '/finance/transactions/export',
    filename: 'transactions',
    icon: BookOpen,
    searchPlaceholder: 'Search description…',
    filters: [
      { kind: 'daterange', label: 'Period', fromParam: 'from', toParam: 'to' },
      {
        kind: 'select',
        param: 'type',
        label: 'Types',
        options: [
          { value: 'income', label: 'Income' },
          { value: 'expense', label: 'Expenses' },
        ],
      },
      {
        kind: 'select',
        param: 'source',
        label: 'Sources',
        options: Object.entries(SOURCE_LABELS).map(([value, label]) => ({ value, label })),
      },
    ],
  },
  {
    id: 'floorplan',
    flow: 'finance',
    label: 'Floor-Plan Loans',
    description: 'Active floor-plan loans with curtailment and interest balances.',
    endpoint: '/finance/floorplan/export',
    filename: 'floorplan-loans',
    icon: Landmark,
    filters: [],
  },
  {
    id: 'invoices',
    flow: 'finance',
    label: 'Invoices',
    description: 'Accounts-receivable invoices with totals and balances.',
    endpoint: '/finance/invoices/export',
    filename: 'invoices',
    icon: Receipt,
    searchPlaceholder: 'Search number or customer…',
    filters: [
      {
        kind: 'select',
        param: 'status',
        label: 'Statuses',
        options: Object.entries(INVOICE_STATUS_LABELS).map(([value, label]) => ({ value, label })),
      },
      {
        kind: 'select',
        param: 'source',
        label: 'Sources',
        options: [
          { value: 'storage', label: 'Storage' },
          { value: 'work_order', label: 'Work Order' },
          { value: 'boat_sale', label: 'Boat Sale' },
          { value: 'manual', label: 'Manual' },
        ],
      },
    ],
  },
  {
    id: 'payments',
    flow: 'finance',
    label: 'Payments',
    description: 'Payments received across invoices, with method and reference.',
    endpoint: '/finance/payments/export',
    filename: 'payments',
    icon: CreditCard,
    filters: [{ kind: 'daterange', label: 'Received', fromParam: 'from', toParam: 'to' }],
  },
  {
    id: 'registrations',
    flow: 'sales',
    label: 'Registrations',
    description: 'Title & registration workflow for sold boats.',
    endpoint: '/registrations/export',
    filename: 'registrations',
    icon: FileCheck,
    filters: [
      {
        kind: 'select',
        param: 'status',
        label: 'Statuses',
        options: Object.entries(REGISTRATION_STATUS_LABELS).map(([value, label]) => ({
          value,
          label,
        })),
      },
    ],
  },
  {
    id: 'leads',
    flow: 'marketing',
    label: 'Leads',
    description: 'Captured leads with source, channel, and conversion status.',
    endpoint: '/marketing/leads/export',
    filename: 'leads',
    icon: Megaphone,
    filters: [
      {
        kind: 'select',
        param: 'status',
        label: 'Statuses',
        options: Object.entries(LEAD_STATUS_LABELS).map(([value, label]) => ({ value, label })),
      },
    ],
  },
  {
    id: 'inspections',
    flow: 'inspection',
    label: 'Inspections',
    description: 'Intake inspection log with engine snapshots and linked work orders.',
    endpoint: '/inspection/export',
    filename: 'inspections',
    icon: ClipboardCheck,
    searchPlaceholder: 'Search HIN, make, or model…',
    filters: [],
  },
  {
    id: 'purchase-orders',
    flow: 'service',
    label: 'Purchase Orders',
    description: 'Supplier purchase orders with status, delivery, and totals.',
    endpoint: '/parts/purchase-orders/export',
    filename: 'purchase-orders',
    icon: ShoppingCart,
    filters: [
      {
        kind: 'select',
        param: 'status',
        label: 'Statuses',
        options: [
          { value: 'draft', label: 'Draft' },
          { value: 'sent', label: 'Sent' },
          { value: 'received', label: 'Received' },
          { value: 'canceled', label: 'Canceled' },
        ],
      },
    ],
  },
]

// Translate the page's filter state into the endpoint's query params, skipping
// unset values ("all" selects, unchecked boxes, empty search).
export function buildReportParams(
  report: ReportDef,
  values: ReportFilterValues,
): Record<string, string | boolean | undefined> {
  const params: Record<string, string | boolean | undefined> = {}
  if (report.searchPlaceholder && typeof values.q === 'string' && values.q !== '') {
    params.q = values.q
  }
  for (const filter of report.filters) {
    if (filter.kind === 'select') {
      const value = values[filter.param]
      if (typeof value === 'string' && value !== 'all') {
        params[filter.param] = value
      }
    } else if (filter.kind === 'daterange') {
      const from = values[filter.fromParam]
      if (typeof from === 'string' && from !== '') {
        params[filter.fromParam] = from
      }
      const to = values[filter.toParam]
      if (typeof to === 'string' && to !== '') {
        params[filter.toParam] = to
      }
    } else if (values[filter.param] === true) {
      params[filter.param] = true
    }
  }
  return params
}
